/**
 * Token Engine Integration Service
 *
 * This service integrates the token system with the existing palette engine,
 * providing seamless conversion between palettes and design tokens.
 */

import { db, type PaletteRecord } from "../lib/database";
import { colorEngine } from "../lib/colorEngine";
import { PaletteToTokenConverter } from "../lib/paletteToTokenConverter";
import { CSSVariablesAdapter } from "../lib/adapters/cssVariablesAdapter";
import {
  TokenSet,
  TokenGenerationOptions,
  TokenExportResult,
  ValidationResult,
  DesignToken,
  TokenGroup,
  TokenDependencyGraph,
  ValidationError,
  ValidationWarning,
} from "../types/designTokens";
import {
  tokenDependencyResolver,
  TokenResolutionResult,
} from "./tokenDependencyResolver";

/**
 * Token generation result with enhanced metadata
 */
export interface TokenGenerationResult {
  tokenSet: TokenSet;
  validation: ValidationResult;
  palette: PaletteRecord;
  generatedAt: number;
  options: TokenGenerationOptions;
  dependencyResolution?: TokenResolutionResult;
}

/**
 * Token export options with platform-specific settings
 */
export interface TokenExportOptions {
  platform: "css" | "json" | "scss" | "js" | "swift" | "android" | "flutter";
  format?: string;
  includeMetadata?: boolean;
  includeComments?: boolean;
  minify?: boolean;
  namingConvention?: "camelCase" | "kebab-case" | "snake_case" | "PascalCase";
  prefix?: string;
  customTemplates?: Record<string, string>;
}

/**
 * Token palette integration options
 */
export interface TokenPaletteIntegrationOptions {
  autoGenerateTokens?: boolean;
  defaultNamingConvention?: TokenGenerationOptions["namingConvention"];
  includeSemanticTokens?: boolean;
  includeAliases?: boolean;
  syncWithPaletteChanges?: boolean;
}

/**
 * Token Engine Service
 *
 * This service provides the main integration point between the palette engine
 * and the token system, handling conversion, validation, and export.
 */
export class TokenEngineService {
  private cssAdapter = new CSSVariablesAdapter();
  private integrationOptions: TokenPaletteIntegrationOptions;

  constructor(options: TokenPaletteIntegrationOptions = {}) {
    this.integrationOptions = {
      autoGenerateTokens: false,
      defaultNamingConvention: "systematic",
      includeSemanticTokens: true,
      includeAliases: true,
      syncWithPaletteChanges: false,
      ...options,
    };
  }

  /**
   * Generate tokens from a palette
   */
  async generateTokensFromPalette(
    paletteId: string,
    options: Partial<TokenGenerationOptions> = {},
  ): Promise<TokenGenerationResult> {
    // Get palette from database
    const palette = await db.getPalette(paletteId);
    if (!palette) {
      throw new Error(`Palette with ID ${paletteId} not found`);
    }

    // Merge with default options
    const generationOptions: TokenGenerationOptions = {
      sourcePalette: palette.name,
      namingConvention: this.integrationOptions.defaultNamingConvention,
      includeSemanticTokens: this.integrationOptions.includeSemanticTokens,
      includeAliases: this.integrationOptions.includeAliases,
      ...options,
    };

    // Generate tokens using the converter (cast to SPA's TokenSet type)
    const tokenSet = PaletteToTokenConverter.convertPalette(
      palette,
      generationOptions,
    ) as unknown as TokenSet;

    // Validate tokens
    const validation = PaletteToTokenConverter.validateTokens(tokenSet as any);

    // Resolve token dependencies
    let dependencyResolution: TokenResolutionResult | undefined;
    try {
      dependencyResolution = await tokenDependencyResolver.resolveTokenSet(
        tokenSet,
        {
          maxDepth: 50,
          throwOnCircular: false,
          resolveNested: true,
        },
      );
    } catch (error) {
      console.warn("Token dependency resolution failed:", error);
      // Continue without dependency resolution if it fails
    }

    // Use resolved token set if dependency resolution succeeded
    const finalTokenSet = dependencyResolution?.tokenSet || tokenSet;

    // Enhance token set with palette metadata
    if (finalTokenSet.$metadata) {
      (finalTokenSet.$metadata as any).paletteId = paletteId;
      (finalTokenSet.$metadata as any).paletteVersion = palette.version;
      finalTokenSet.$metadata.paletteModified = new Date(
        palette.modified,
      ).toISOString();
      finalTokenSet.$metadata.colorCount = palette.colors.length;
      finalTokenSet.$metadata.tags = palette.tags;
    }

    // Combine validation results
    const combinedValidation: ValidationResult = {
      valid:
        validation.valid && (dependencyResolution?.validation.valid ?? true),
      errors: [
        ...validation.errors.map((e: { path: string; message: string; code: string }) => ({ ...e, severity: 'error' as const })),
        ...(dependencyResolution?.validation.errors ?? []),
      ],
      warnings: [
        ...validation.warnings.map((w: { path: string; message: string; code: string }) => ({ ...w, severity: 'warning' as const })),
        ...(dependencyResolution?.validation.warnings ?? []),
      ],
    };

    return {
      tokenSet: finalTokenSet,
      validation: combinedValidation,
      palette,
      generatedAt: Date.now(),
      options: generationOptions,
      dependencyResolution,
    };
  }

  /**
   * Generate tokens from multiple palettes
   */
  async generateTokensFromPalettes(
    paletteIds: string[],
    options: Partial<TokenGenerationOptions> = {},
  ): Promise<TokenGenerationResult[]> {
    const results: TokenGenerationResult[] = [];

    for (const paletteId of paletteIds) {
      try {
        const result = await this.generateTokensFromPalette(paletteId, options);
        results.push(result);
      } catch (error) {
        console.error(
          `Failed to generate tokens for palette ${paletteId}:`,
          error,
        );
        // Continue with other palettes
      }
    }

    return results;
  }

  /**
   * Export tokens to specified platform
   */
  async exportTokens(
    tokenSet: TokenSet,
    exportOptions: TokenExportOptions,
  ): Promise<TokenExportResult> {
    switch (exportOptions.platform) {
      case "css":
        return this.cssAdapter.export(tokenSet, {
          format: exportOptions.format || "css",
          includeMetadata: exportOptions.includeMetadata,
          includeComments: exportOptions.includeComments,
          minify: exportOptions.minify,
          namingConvention: exportOptions.namingConvention,
          prefix: exportOptions.prefix,
          customTemplates: exportOptions.customTemplates,
        });

      case "json":
        return this.exportAsJSON(tokenSet, exportOptions);

      default:
        throw new Error(
          `Platform ${exportOptions.platform} is not yet supported`,
        );
    }
  }

  /**
   * Export tokens as JSON
   */
  private async exportAsJSON(
    tokenSet: TokenSet,
    options: TokenExportOptions,
  ): Promise<TokenExportResult> {
    const content = JSON.stringify(tokenSet, null, options.minify ? 0 : 2);
    const filename = this.generateFilename(tokenSet, "json", options);

    return {
      platform: "JSON",
      format: "json",
      content,
      filename,
      size: new Blob([content]).size,
      timestamp: new Date().toISOString(),
      tokens: this.countTokens(tokenSet),
    };
  }

  /**
   * Get available palettes for token generation
   */
  async getAvailablePalettes(): Promise<PaletteRecord[]> {
    return await db.getAllPalettes();
  }

  /**
   * Search palettes for token generation
   */
  async searchPalettes(query: string): Promise<PaletteRecord[]> {
    return await db.searchPalettes(query);
  }

  /**
   * Validate token set against DTCG specification
   */
  validateTokenSet(tokenSet: TokenSet): ValidationResult {
    const baseValidation = this.cssAdapter.validate(tokenSet);

    // Add enhanced color format validation
    const colorValidation = this.validateColorFormats(tokenSet);

    return {
      valid: baseValidation.valid && colorValidation.valid,
      errors: [...baseValidation.errors, ...colorValidation.errors],
      warnings: [...baseValidation.warnings, ...colorValidation.warnings],
    };
  }

  /**
   * Validate color formats in token set
   */
  private validateColorFormats(tokenSet: TokenSet): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];

    const validateColorInGroup = (
      group: TokenGroup | DesignToken,
      path: string,
    ): void => {
      if (this.isTokenGroup(group)) {
        const tokenGroup = group as TokenGroup;
        if (tokenGroup.children) {
          Object.entries(tokenGroup.children).forEach(([key, value]) => {
            const childPath = path ? `${path}.${key}` : key;
            validateColorInGroup(value as TokenGroup | DesignToken, childPath);
          });
        }
      } else {
        const token = group as DesignToken;

        if (token.$type === "color") {
          const colorValidation = this.validateSingleColor(token, path);
          errors.push(...colorValidation.errors);
          warnings.push(...colorValidation.warnings);
        }
      }
    };

    if (tokenSet.tokens) {
      validateColorInGroup(tokenSet.tokens, "tokens");
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Validate a single color token
   */
  private validateSingleColor(
    token: DesignToken,
    path: string,
  ): {
    errors: ValidationError[];
    warnings: ValidationWarning[];
  } {
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];

    // Check if it's a token reference
    if (typeof token.$value === "string" && token.$value.startsWith("{")) {
      const refPath = token.$value.slice(1, -1);

      // Validate reference path format
      if (!/^[a-zA-Z][a-zA-Z0-9._-]*$/.test(refPath)) {
        errors.push({
          path,
          message: `Invalid token reference format: "${refPath}"`,
          code: "INVALID_TOKEN_REFERENCE",
          severity: "error",
        });
      }

      return { errors, warnings };
    }

    // Validate color value structure
    if (typeof token.$value === "object" && token.$value !== null) {
      const colorValue = token.$value as any;

      // Check required properties
      if (!colorValue.colorSpace) {
        errors.push({
          path,
          message: 'Color token missing required "colorSpace" property',
          code: "MISSING_COLOR_SPACE",
          severity: "error",
        });
      }

      if (!colorValue.channels) {
        errors.push({
          path,
          message: 'Color token missing required "channels" property',
          code: "MISSING_COLOR_CHANNELS",
          severity: "error",
        });
      }

      // Validate color space
      if (
        colorValue.colorSpace &&
        !this.isValidColorSpace(colorValue.colorSpace)
      ) {
        errors.push({
          path,
          message: `Unsupported color space: "${colorValue.colorSpace}"`,
          code: "UNSUPPORTED_COLOR_SPACE",
          severity: "error",
        });
      }

      // Validate channels
      if (colorValue.channels && Array.isArray(colorValue.channels)) {
        if (colorValue.channels.length < 3 || colorValue.channels.length > 4) {
          errors.push({
            path,
            message: `Color channels must have 3 (RGB) or 4 (RGBA) values, got ${colorValue.channels.length}`,
            code: "INVALID_CHANNEL_COUNT",
            severity: "error",
          });
        } else {
          // Validate each channel value
          colorValue.channels.forEach((channel: number, index: number) => {
            if (typeof channel !== "number" || !isFinite(channel)) {
              errors.push({
                path,
                message: `Channel ${index} must be a finite number, got ${channel}`,
                code: "INVALID_CHANNEL_VALUE",
                severity: "error",
              });
            } else if (channel < 0 || channel > 1) {
              errors.push({
                path,
                message: `Channel ${index} must be between 0 and 1, got ${channel}`,
                code: "CHANNEL_OUT_OF_RANGE",
                severity: "error",
              });
            }
          });
        }
      }

      // Validate alpha if present
      if (colorValue.alpha !== undefined) {
        if (
          typeof colorValue.alpha !== "number" ||
          !isFinite(colorValue.alpha)
        ) {
          errors.push({
            path,
            message: `Alpha must be a finite number, got ${colorValue.alpha}`,
            code: "INVALID_ALPHA_VALUE",
            severity: "error",
          });
        } else if (colorValue.alpha < 0 || colorValue.alpha > 1) {
          errors.push({
            path,
            message: `Alpha must be between 0 and 1, got ${colorValue.alpha}`,
            code: "ALPHA_OUT_OF_RANGE",
            severity: "error",
          });
        } else if (colorValue.alpha === 1) {
          warnings.push({
            path,
            message: "Alpha value of 1 is unnecessary (opaque is default)",
            code: "UNNECESSARY_ALPHA",
            severity: "warning",
          });
        }
      }
    } else {
      errors.push({
        path,
        message: "Color token value must be an object or token reference",
        code: "INVALID_COLOR_VALUE_TYPE",
        severity: "error",
      });
    }

    return { errors, warnings };
  }

  /**
   * Check if color space is valid
   */
  private isValidColorSpace(colorSpace: string): boolean {
    const validSpaces = [
      "srgb",
      "srgb-linear",
      "p3",
      "rec2020",
      "a98-rgb",
      "prophoto-rgb",
    ];
    return validSpaces.includes(colorSpace);
  }

  /**
   * Get color analysis for palette tokens
   */
  async getColorAnalysis(paletteId: string): Promise<{
    palette: PaletteRecord;
    colorAnalysis: Array<{
      hex: string;
      luminance: number;
      hue: string | null;
      lightnessCategory: string;
      accessibility: {
        aaLarge: boolean;
        aa: boolean;
        aaaLarge: boolean;
        aaa: boolean;
      };
    }>;
  }> {
    const palette = await db.getPalette(paletteId);
    if (!palette) {
      throw new Error(`Palette with ID ${paletteId} not found`);
    }

    const colorAnalysis = palette.colors.map((hex) => {
      const colorResult = colorEngine.parseColor(hex);
      if (!colorResult) {
        throw new Error(`Failed to parse color: ${hex}`);
      }

      const luminance = this.calculateLuminance(hex);
      const hue = this.getHue(hex);
      const lightnessCategory = this.getLightnessCategory(luminance);

      return {
        hex,
        luminance,
        hue,
        lightnessCategory,
        accessibility: {
          aaLarge: this.checkContrastRatio(hex, "#ffffff", 3.0),
          aa: this.checkContrastRatio(hex, "#ffffff", 4.5),
          aaaLarge: this.checkContrastRatio(hex, "#ffffff", 4.5),
          aaa: this.checkContrastRatio(hex, "#ffffff", 7.0),
        },
      };
    });

    return {
      palette,
      colorAnalysis,
    };
  }

  /**
   * Generate semantic token suggestions based on color analysis
   */
  generateSemanticSuggestions(
    colorAnalysis: Array<{
      hex: string;
      luminance: number;
      hue: string | null;
      lightnessCategory: string;
    }>,
  ): Record<string, string> {
    const suggestions: Record<string, string> = {};

    // Find primary colors (most saturated/vibrant)
    const vibrantColors = colorAnalysis
      .filter((c) => c.luminance > 0.2 && c.luminance < 0.8)
      .sort((a, b) => b.luminance - a.luminance);

    if (vibrantColors.length > 0) {
      suggestions.primary = vibrantColors[0].hex;
    }

    // Find background colors (light colors)
    const lightColors = colorAnalysis
      .filter((c) => c.luminance > 0.8)
      .sort((a, b) => b.luminance - a.luminance);

    if (lightColors.length > 0) {
      suggestions.background = lightColors[0].hex;
      suggestions["surface"] = lightColors[0].hex;
    }

    // Find text colors (dark colors)
    const darkColors = colorAnalysis
      .filter((c) => c.luminance < 0.3)
      .sort((a, b) => a.luminance - b.luminance);

    if (darkColors.length > 0) {
      suggestions["text-primary"] = darkColors[0].hex;
    }

    // Find accent colors by hue
    const colorsByHue = colorAnalysis.reduce(
      (acc, color) => {
        if (color.hue) {
          if (!acc[color.hue]) acc[color.hue] = [];
          acc[color.hue].push(color);
        }
        return acc;
      },
      {} as Record<string, typeof colorAnalysis>,
    );

    Object.entries(colorsByHue).forEach(([hue, colors]) => {
      if (colors.length > 0) {
        const mostVibrant = colors
          .filter((c) => c.luminance > 0.3 && c.luminance < 0.7)
          .sort((a, b) => b.luminance - a.luminance)[0];

        if (mostVibrant) {
          suggestions[`accent-${hue}`] = mostVibrant.hex;
        }
      }
    });

    return suggestions;
  }

  /**
   * Sync tokens with palette changes
   */
  async syncTokensWithPalette(
    paletteId: string,
  ): Promise<TokenGenerationResult | null> {
    if (!this.integrationOptions.syncWithPaletteChanges) {
      return null;
    }

    try {
      return await this.generateTokensFromPalette(paletteId, {
        namingConvention: this.integrationOptions.defaultNamingConvention,
        includeSemanticTokens: this.integrationOptions.includeSemanticTokens,
        includeAliases: this.integrationOptions.includeAliases,
      });
    } catch (error) {
      console.error(`Failed to sync tokens for palette ${paletteId}:`, error);
      return null;
    }
  }

  /**
   * Get token generation history for a palette
   */
  async getTokenGenerationHistory(paletteId: string): Promise<
    Array<{
      timestamp: number;
      options: TokenGenerationOptions;
      tokenCount: number;
      validation: ValidationResult;
    }>
  > {
    // This would integrate with a token generation history table
    // For now, return empty array
    return [];
  }

  /**
   * Resolve token dependencies for an existing token set
   */
  async resolveTokenDependencies(
    tokenSet: TokenSet,
    options: {
      maxDepth?: number;
      throwOnCircular?: boolean;
      resolveNested?: boolean;
    } = {},
  ): Promise<TokenResolutionResult> {
    return await tokenDependencyResolver.resolveTokenSet(tokenSet, {
      maxDepth: options.maxDepth ?? 50,
      throwOnCircular: options.throwOnCircular ?? false,
      resolveNested: options.resolveNested ?? true,
    });
  }

  /**
   * Get dependency analysis for a token set
   */
  async getDependencyAnalysis(tokenSet: TokenSet): Promise<{
    totalTokens: number;
    tokenReferences: number;
    circularDependencies: string[];
    maxDepth: number;
    dependencyGraph: TokenDependencyGraph;
  }> {
    const dependencyGraph =
      tokenDependencyResolver.buildDependencyGraph(tokenSet);
    const circularDependencies =
      tokenDependencyResolver.detectCircularDependencies(dependencyGraph);

    let tokenReferences = 0;
    let maxDepth = 0;

    for (const [path, tokenRef] of dependencyGraph.nodes) {
      if (!tokenRef.resolved) {
        tokenReferences++;
      }

      const depth = this.calculateReferenceDepth(path, dependencyGraph);
      maxDepth = Math.max(maxDepth, depth);
    }

    return {
      totalTokens: dependencyGraph.nodes.size,
      tokenReferences,
      circularDependencies: Array.from(circularDependencies),
      maxDepth,
      dependencyGraph,
    };
  }

  // Private helper methods

  private calculateLuminance(hex: string): number {
    const colorValue = PaletteToTokenConverter["hexToColorValue"](hex);
    const [r, g, b] = colorValue.channels;
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  private getHue(hex: string): string | null {
    return PaletteToTokenConverter["getHue"](hex);
  }

  private getLightnessCategory(luminance: number): string {
    if (luminance > 0.8) return "very-light";
    if (luminance > 0.6) return "light";
    if (luminance > 0.4) return "medium";
    if (luminance > 0.2) return "dark";
    return "very-dark";
  }

  private checkContrastRatio(
    color1: string,
    color2: string,
    threshold: number,
  ): boolean {
    try {
      const ratio = colorEngine.calculateDeltaE(color1, color2, "2000");
      // This is a simplified check - real contrast ratio calculation is more complex
      return ratio !== null; // Placeholder
    } catch {
      return false;
    }
  }

  private calculateReferenceDepth(
    path: string,
    dependencyGraph: TokenDependencyGraph,
  ): number {
    const visited = new Set<string>();

    const calculateDepth = (currentPath: string, depth: number = 0): number => {
      if (visited.has(currentPath)) {
        return depth; // Circular reference detected
      }

      visited.add(currentPath);

      const dependencies = dependencyGraph.edges.get(currentPath);
      if (!dependencies || dependencies.size === 0) {
        return depth;
      }

      let maxChildDepth = depth;
      for (const dep of dependencies) {
        maxChildDepth = Math.max(maxChildDepth, calculateDepth(dep, depth + 1));
      }

      return maxChildDepth;
    };

    return calculateDepth(path);
  }

  private generateFilename(
    tokenSet: TokenSet,
    format: string,
    options: TokenExportOptions,
  ): string {
    const baseName = tokenSet.$metadata?.name || "tokens";
    const formattedName = baseName.toLowerCase().replace(/\s+/g, "-");
    const suffix = options.platform === "css" ? "-variables" : "";
    return `${formattedName}${suffix}.${format}`;
  }

  private countTokens(tokenSet: TokenSet): number {
    let count = 0;

    const countInGroup = (group: TokenGroup | DesignToken): number => {
      if (this.isTokenGroup(group)) {
        const tokenGroup = group as TokenGroup;
        let groupCount = 0;
        if (tokenGroup.children) {
          Object.values(tokenGroup.children).forEach((value) => {
            groupCount += countInGroup(value as TokenGroup | DesignToken);
          });
        }
        return groupCount;
      } else {
        return 1;
      }
    };

    if (tokenSet.tokens) {
      count += countInGroup(tokenSet.tokens);
    }

    return count;
  }

  private isTokenGroup(value: any): boolean {
    return (
      value &&
      typeof value === "object" &&
      (!value.$type || value.$type === "group")
    );
  }
}

// Export singleton instance
export const tokenEngineService = new TokenEngineService();
